import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Loader2, ArrowUpDown, Check, ChevronsUpDown, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableSensorsByAccount, useAccountSensorFilterOptions } from '@/features/quotes/hooks';
import type { QuoteSensor, SensorSelectParams, SensorFilterOption } from '@/features/quotes/types';

const SENSOR_STATUS_LABELS: Record<number, { label: string; variant: 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' }> = {
  0: { label: 'Unmanaged', variant: 'secondary' },
  1: { label: 'Active', variant: 'success' },
  2: { label: 'Too Low', variant: 'warning' },
  3: { label: 'Too High', variant: 'warning' },
  4: { label: 'To Acknowledge', variant: 'warning' },
  5: { label: 'Inactive', variant: 'destructive' },
  6: { label: 'Maintenance', variant: 'outline' },
  8: { label: 'Inhibited', variant: 'secondary' },
  9: { label: 'Archived', variant: 'secondary' },
};

const STATUS_FILTER_OPTIONS = [
  { value: 0, label: 'Unmanaged' },
  { value: 1, label: 'Active' },
  { value: 2, label: 'Too Low' },
  { value: 3, label: 'Too High' },
  { value: 4, label: 'To Acknowledge' },
  { value: 5, label: 'Inactive' },
  { value: 6, label: 'Maintenance' },
  { value: 8, label: 'Inhibited' },
  { value: 9, label: 'Archived' },
];

interface SensorSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pgAccountId: string;
  linkedSensorIds: Set<string>;
  onAddSensor: (sensor: QuoteSensor) => void;
  onRemoveSensor: (sensorId: string) => void;
}

/**
 * Single-select combobox over one filter facet (#1177). The trigger shows the
 * facet label, then the picked option's name; the X button clears it. Options
 * come precomputed from /v4/accounts/:id/sensor-filter-options (a subset of
 * the candidate population), so cmdk can filter them client-side.
 */
function FilterCombobox({
  label, clearLabel, options, value, onChange,
}: {
  label: string;
  clearLabel: string;
  options: SensorFilterOption[];
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const { t } = useTranslation('subscriptions');
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.id === value);

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={selected ? selected.name : label}
            className="h-8 text-xs justify-between font-normal"
          >
            <span className={cn('truncate', !selected && 'text-muted-foreground')}>
              {selected ? selected.name : label}
            </span>
            <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder={label} />
            <CommandList>
              <CommandEmpty>{t('sensors.noFilterOptions', 'No options')}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    onSelect={() => {
                      onChange(option.id);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === option.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{option.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={clearLabel}
          onClick={() => onChange(undefined)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export function SensorSelectDialog({
  open,
  onOpenChange,
  pgAccountId,
  linkedSensorIds,
  onAddSensor,
  onRemoveSensor,
}: SensorSelectDialogProps) {
  const { t } = useTranslation('subscriptions');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [statusFilters, setStatusFilters] = useState<number[]>([]);
  const [pageLength, setPageLength] = useState(25);
  const [usergroupId, setUsergroupId] = useState<string | undefined>(undefined);
  const [sensorgroupId, setSensorgroupId] = useState<string | undefined>(undefined);
  const [siteId, setSiteId] = useState<string | undefined>(undefined);

  const debouncedSearch = useDebounce(search, 300);

  const params: SensorSelectParams = {
    pageNumber: page,
    pageLength,
    sortKey,
    sortDirection,
    search: debouncedSearch || undefined,
    status_in: statusFilters.length > 0 ? statusFilters : undefined,
    usergroupId: usergroupId || undefined,
    sensorgroupId: sensorgroupId || undefined,
    siteId: siteId || undefined,
  };

  const { data, isLoading } = useSelectableSensorsByAccount(
    open ? pgAccountId : undefined,
    params,
  );
  const { data: filterOptions } = useAccountSensorFilterOptions(
    open ? pgAccountId : undefined,
  );
  const options = filterOptions || { usergroups: [], sensorgroups: [], sites: [] };
  const sensors = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / pageLength);

  const toggleStatus = (status: number) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(0);
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const selectableOnPage = useMemo(
    () => sensors.filter((s) => !linkedSensorIds.has(s._id)),
    [sensors, linkedSensorIds],
  );

  const handleSelectAll = () => {
    selectableOnPage.forEach((s) => onAddSensor(s));
  };

  const SortHead = ({ label, field }: { label: string; field: string }) => (
    <th
      className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </th>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('sensors.selectSensors')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder={t('sensors.filter')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="h-8 text-sm max-w-[200px]"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  {statusFilters.length > 0 ? `Status (${statusFilters.length})` : 'Status'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt.value}
                    checked={statusFilters.includes(opt.value)}
                    onCheckedChange={() => toggleStatus(opt.value)}
                  >
                    {opt.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <FilterCombobox
              label={t('sensors.filterUsergroup', 'User group')}
              clearLabel={t('sensors.clearUsergroupFilter', 'Clear user group filter')}
              options={options.usergroups}
              value={usergroupId}
              onChange={(id) => {
                setUsergroupId(id);
                setPage(0);
              }}
            />
            <FilterCombobox
              label={t('sensors.filterSensorgroup', 'Sensor group')}
              clearLabel={t('sensors.clearSensorgroupFilter', 'Clear sensor group filter')}
              options={options.sensorgroups}
              value={sensorgroupId}
              onChange={(id) => {
                setSensorgroupId(id);
                setPage(0);
              }}
            />
            <FilterCombobox
              label={t('sensors.filterSite', 'Site')}
              clearLabel={t('sensors.clearSiteFilter', 'Clear site filter')}
              options={options.sites}
              value={siteId}
              onChange={(id) => {
                setSiteId(id);
                setPage(0);
              }}
            />
            {selectableOnPage.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('sensors.selectAll')} ({selectableOnPage.length})
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <SortHead label={t('sensors.customId')} field="customid" />
                  <SortHead label={t('sensors.name')} field="name" />
                  <SortHead label={t('sensors.location')} field="location.name" />
                  <SortHead label={t('sensors.stream')} field="stream.name" />
                  <SortHead label={t('sensors.status')} field="status" />
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : sensors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      {t('sensors.noResults')}
                    </td>
                  </tr>
                ) : (
                  sensors.map((sensor) => {
                    const isLinked = linkedSensorIds.has(sensor._id);
                    return (
                      <tr key={sensor._id} className="border-t">
                        <td className="px-2 py-1.5 text-xs">{sensor.customid || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs font-medium">{sensor.name}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.location?.name || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.stream?.name || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">
                          {sensor.status != null ? (() => {
                            const statusInfo = SENSOR_STATUS_LABELS[sensor.status];
                            return (
                              <Badge variant={statusInfo?.variant ?? 'outline'} className="text-[10px]">
                                {statusInfo?.label ?? sensor.status}
                              </Badge>
                            );
                          })() : '\u2014'}
                        </td>
                        <td className="px-2 py-1.5">
                          {isLinked ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => onRemoveSensor(sensor._id)}
                              title={t('sensors.removeFromSubscription')}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-green-600 hover:text-green-700"
                              onClick={() => onAddSensor(sensor)}
                              title={t('sensors.addToSubscription')}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {total} {t('sensors.sensorsTotal')}
                </span>
                <select
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                  value={pageLength}
                  onChange={(e) => {
                    setPageLength(Number(e.target.value));
                    setPage(0);
                  }}
                  aria-label={t('table.perPage')}
                >
                  {[25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} / {t('table.perPage')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  {t('table.previous')}
                </Button>
                <span className="flex items-center px-2 text-muted-foreground">
                  {page + 1} / {Math.max(totalPages, 1)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  {t('table.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
